import lodash from "lodash";
import * as mathjs from "mathjs";

import { findClosestNote } from "@/lib/key";
import { Marker } from "@/types/defs/pitchCorrection/marker";

// Markers closer together than this many frames are thinned out, and a non-endpoint marker this close to the following endpoint is merged into it.
const MIN_MARKER_GAP_FRAMES = 10;

const mergeRightmostCircles = (circles: Marker[], gap: number) => {
  if (circles.length === 0) {
    return [];
  }

  const result: Marker[] = [circles[0]];

  for (let i = 1; i < circles.length; i++) {
    const circle = circles[i];
    if (
      circle.isEndpoint &&
      !result[result.length - 1].isEndpoint &&
      circle.index - result[result.length - 1].index < gap
    ) {
      result.pop();
    }
    result.push(circle);
  }

  return result;
};

const thinOutCircles = (circles: Marker[], gap: number): Marker[] => {
  if (circles.length === 0) {
    return [];
  }

  const result: Marker[] = [];

  for (let i = 0; i < circles.length; i++) {
    if (result.length === 0) {
      result.push(circles[i]);
      continue;
    } else {
      const circle = circles[i];
      if (
        circle.index - result[result.length - 1].index > gap ||
        circle.isEndpoint
      ) {
        result.push(circle);
      }
    }
  }

  return result;
};

const groupAsMarkers = (vals: number[]): Marker[] => {
  const markers: Marker[] = [];

  // Add the initial marker if the first value is non-zero
  if (vals[0] !== 0) {
    markers.push({ index: 0, isEndpoint: true });
  }

  for (let i = 1; i < vals.length; i++) {
    if (vals[i] !== vals[i - 1]) {
      // If there is a change in value
      if (vals[i] === 0 || vals[i - 1] === 0) {
        // One of the values is zero
        const nonZeroIndex = vals[i] === 0 ? i - 1 : i;
        markers.push({ index: nonZeroIndex, isEndpoint: true });
      } else {
        // Both values are non-zero
        markers.push({ index: i, isEndpoint: false });
      }
    }
  }

  // Add the final marker if the last value is non-zero
  if (vals[vals.length - 1] !== 0) {
    markers.push({ index: vals.length - 1, isEndpoint: true });
  }

  return markers;
};

const newClosestPitchAlgo = (
  validNotes: number[],
  pitches: number[],
  windowLength: number
) => {
  // Function to create windows and calculate shifts
  const calculateClosestPitch = (index: number): number => {
    const halfWindow = Math.floor(windowLength / 2);
    const windows: number[][] = [];

    // Create windows around the current pitch, centered on the index
    for (let i = -halfWindow; i <= halfWindow; i++) {
      const windowStart = index + i - halfWindow;
      const windowEnd = index + i + halfWindow;

      // Ensure the window is within bounds and filter out zeros
      const window = pitches
        .slice(
          Math.max(0, windowStart),
          Math.min(pitches.length, windowEnd + 1)
        )
        .filter((p) => p !== 0);
      if (window.length > 0) {
        windows.push(window);
      }
    }

    // Calculate shifts for each window
    const closestNotes: number[] = windows.map((window) => {
      const average = window.reduce((sum, val) => sum + val, 0) / window.length;
      const closestNote = findClosestNote(validNotes, average);
      return closestNote;
    });

    // Calculate the average shift
    const closestNote =
      closestNotes.length === 0 ? 0 : mathjs.mode(closestNotes)[0];

    return Number.isNaN(closestNote) ? 0 : closestNote;
  };

  // Process each pitch in the array
  const shiftedPitches = pitches.map((pitch, index) =>
    pitch === 0 ? 0 : calculateClosestPitch(index)
  );
  return shiftedPitches;
};

// Lives outside PitchCorrection.tsx so server code (the harmonies API) can segment a pitch contour without importing the editor component, which a server route cannot load.
export const getSegmentationPoints2 = (semitoneData: number[]) =>
  mergeRightmostCircles(
    thinOutCircles(
      groupAsMarkers(
        newClosestPitchAlgo(lodash.range(30, 140, 0.5), semitoneData, 5)
      ).sort((a, b) => a.index - b.index),
      MIN_MARKER_GAP_FRAMES
    ),
    MIN_MARKER_GAP_FRAMES
  );
